/**
 * API 서비스 모듈
 *
 * 백엔드 API와 통신하는 함수들을 모아놓은 모듈입니다.
 * axios를 사용하여 HTTP 요청을 보냅니다.
 *
 * API 기본 URL: /api/v1 (Vite proxy 사용)
 *
 * 주요 엔드포인트:
 * - POST /humming/search     : 허밍 검색 (오디오 파일 업로드)
 * - GET  /lyrics/search      : 가사 검색 (발음 검색 포함)
 * - GET  /search             : 트랙 검색 (ElasticSearch)
 * - GET  /search/autocomplete: 자동완성
 */

import axios from 'axios'
import type {
  SearchResponse,
  AutocompleteResponse,
  TrackListResponse,
  StatsResponse,
  HummingSearchResponse,
  LyricsSearchResponse
} from '../types'

// =============================================================================
// API 클라이언트 설정
// =============================================================================

/**
 * API 기본 URL
 *
 * 개발 환경: Vite dev server가 /api 요청을 백엔드로 프록시
 * 프로덕션: VITE_API_URL 환경변수 사용 (예: https://searchyourmelody.com)
 */
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

/**
 * Axios 인스턴스
 *
 * 공통 설정을 적용한 axios 클라이언트
 * - baseURL: API 기본 경로
 * - timeout: 요청 타임아웃 (10초)
 */
const client = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 허밍 검색은 오래 걸릴 수 있어서 30초로 설정
})

// =============================================================================
// API 함수들
// =============================================================================

export const api = {
  // ---------------------------------------------------------------------------
  // 허밍 검색 API
  // ---------------------------------------------------------------------------

  /**
   * 허밍으로 곡 검색
   *
   * 녹음된 오디오 파일을 서버로 전송하여 비슷한 곡을 찾습니다.
   *
   * 작동 방식:
   * 1. 오디오 파일 업로드 (multipart/form-data)
   * 2. 서버에서 PYIN으로 피치 추출
   * 3. 160차원 임베딩 벡터 생성
   * 4. ChromaDB에서 코사인 유사도 검색
   * 5. 결과 반환
   *
   * @param audioBlob - 녹음된 오디오 Blob (webm/opus 또는 mp4)
   * @returns 검색 결과 (유사도 순 정렬)
   *
   * @example
   * ```tsx
   * const handleRecordingComplete = async (blob: Blob) => {
   *   try {
   *     const results = await api.searchByHumming(blob)
   *     console.log(`${results.results.length}개 결과 발견`)
   *   } catch (err) {
   *     console.error('검색 실패:', err)
   *   }
   * }
   * ```
   */
  async searchByHumming(audioBlob: Blob, nResults = 20): Promise<HummingSearchResponse> {
    // FormData로 파일 전송 준비
    const formData = new FormData()
    formData.append('audio_file', audioBlob, 'humming.webm')

    // POST 요청 (multipart/form-data)
    const { data } = await client.post<HummingSearchResponse>(
      `/humming/search?n_results=${nResults}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // 업로드 진행 상황은 따로 처리하지 않음
      }
    )

    return data
  },

  // ---------------------------------------------------------------------------
  // 가사 검색 API
  // ---------------------------------------------------------------------------

  /**
   * 가사로 곡 검색
   *
   * 발음 기반 검색을 지원합니다.
   * 예: "와리가저럽" → "What is love" 매칭
   *
   * 작동 방식:
   * 1. 입력 텍스트를 발음 기호로 변환 (phonetic)
   * 2. 저장된 가사의 발음과 비교
   * 3. 유사도 높은 순으로 반환
   *
   * @param query - 검색할 가사 (발음으로 써도 됨)
   * @param limit - 최대 결과 수 (기본 20)
   * @returns 검색 결과
   *
   * @example
   * ```tsx
   * // 한국어 발음으로 검색
   * const results = await api.searchByLyrics('와리가저럽')
   *
   * // 영어로 검색
   * const results = await api.searchByLyrics('love me like')
   * ```
   */
  async searchByLyrics(query: string, limit = 20): Promise<LyricsSearchResponse> {
    const { data } = await client.get<LyricsSearchResponse>('/lyrics/search', {
      params: { q: query, limit }
    })
    return data
  },

  /**
   * 미리듣기 URL 가져오기 (Lazy Loading)
   *
   * 재생 버튼 클릭 시 호출됩니다.
   * Deezer API에서 실시간으로 preview URL을 가져옵니다.
   *
   * @param platformId - Deezer 트랙 ID
   * @returns preview URL
   */
  async getPreviewUrl(platformId: string): Promise<{ platform_id: string; preview_url: string }> {
    const { data } = await client.get(`/lyrics/preview/${platformId}`)
    return data
  },

  // ---------------------------------------------------------------------------
  // 트랙 검색 API (ElasticSearch)
  // ---------------------------------------------------------------------------

  /**
   * 트랙 검색 (ElasticSearch)
   *
   * 아티스트명, 트랙명, 앨범명을 퍼지 검색합니다.
   * 오타도 어느 정도 허용됩니다.
   *
   * @param query - 검색어
   * @param limit - 최대 결과 수 (기본 20)
   * @returns 검색 결과 (관련도 순)
   */
  async searchTracks(query: string, limit = 20): Promise<SearchResponse> {
    const { data } = await client.get<SearchResponse>('/search', {
      params: { q: query, limit }
    })
    return data
  },

  /**
   * 트랙 검색 (Simple - PostgreSQL)
   *
   * ElasticSearch 실패 시 fallback으로 사용합니다.
   * 단순 LIKE 검색이라 정확도는 낮지만 안정적입니다.
   *
   * @param query - 검색어
   * @param limit - 최대 결과 수
   */
  async searchTracksSimple(query: string, limit = 20): Promise<{ results: any[] }> {
    const { data } = await client.get('/tracks/search/simple', {
      params: { q: query, limit }
    })
    return data
  },

  // ---------------------------------------------------------------------------
  // 자동완성 API
  // ---------------------------------------------------------------------------

  /**
   * 자동완성 제안
   *
   * 검색어 입력 중 실시간 제안을 가져옵니다.
   * ElasticSearch의 completion suggester 사용
   *
   * @param query - 입력 중인 검색어
   * @returns 자동완성 제안 목록
   */
  async getAutocomplete(query: string): Promise<AutocompleteResponse> {
    const { data } = await client.get<AutocompleteResponse>('/search/autocomplete', {
      params: { q: query }
    })
    return data
  },

  // ---------------------------------------------------------------------------
  // 기타 API
  // ---------------------------------------------------------------------------

  /**
   * 트랙 목록 조회
   *
   * 페이지네이션으로 전체 트랙 목록을 가져옵니다.
   *
   * @param page - 페이지 번호 (1부터 시작)
   * @param limit - 페이지당 항목 수
   */
  async getTracks(page = 1, limit = 20): Promise<TrackListResponse> {
    const { data } = await client.get<TrackListResponse>('/tracks', {
      params: { page, limit }
    })
    return data
  },

  /**
   * 통계 조회
   *
   * 전체 트랙 수, 평균 인기도, 상위 아티스트 등
   */
  async getStats(): Promise<StatsResponse> {
    const { data } = await client.get<StatsResponse>('/tracks/stats/summary')
    return data
  },

  /**
   * 무드별 검색
   *
   * 오디오 특성 기반 무드 필터링
   *
   * @param mood - 무드 종류 (energetic, calm, etc.)
   * @param limit - 최대 결과 수
   */
  async searchByMood(mood: string, limit = 20): Promise<{ results: any[] }> {
    const { data } = await client.get('/search/by-mood', {
      params: { mood, limit }
    })
    return data
  },

  /**
   * 헬스 체크
   *
   * 서버 상태 확인용
   */
  async healthCheck(): Promise<{ status: string }> {
    const { data } = await client.get('/health')
    return data
  }
}
